import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { Eye, EyeSlash, ArrowRight, House, CheckCircle, CreditCard } from "@phosphor-icons/react";
import { openPaddleCheckout } from "@/lib/paddle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const { register } = useAuth();
  const { t, isRtl } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isSubscribeMode = location.pathname === "/subscribe";
  const nextPath = isSubscribeMode ? "/billing" : searchParams.get("next");

  const [form, setForm] = useState({
    name: "", surname: "", email: "", password: "",
    company_name: "", phone: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Subscribe mode only: if already logged in, pre-fill name/surname/email
  // (read-only) so the user completes the missing company + phone fields.
  const [me, setMe] = useState(null);
  const [prefillDone, setPrefillDone] = useState(!isSubscribeMode);

  useEffect(() => {
    if (!isSubscribeMode) return;
    const token = typeof window !== "undefined" && localStorage.getItem("esg_token");
    if (!token) { setPrefillDone(true); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setMe(data);
        setForm(f => ({
          ...f,
          name: data.name || "",
          surname: data.surname || "",
          email: data.email || "",
          company_name: data.company_name || "",
          phone: data.phone || "",
        }));
      })
      .catch(() => { /* invalid token → treat as anonymous, form stays empty */ })
      .finally(() => setPrefillDone(true));
  }, [isSubscribeMode]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim())    { setError(t('reg_err_firstname')); return; }
    if (!form.surname.trim()) { setError(t('reg_err_surname'));   return; }
    if (isSubscribeMode) {
      if (!form.company_name.trim()) { setError(t('sub_err_company')); return; }
      if (form.phone.replace(/\D/g, "").length < 6) { setError(t('sub_err_phone')); return; }
    }
    const alreadyAuthed = isSubscribeMode && !!me;
    if (!alreadyAuthed && form.password.length < 6) { setError(t('reg_err_password_len')); return; }

    setLoading(true);
    try {
      // Step 1: create account if the visitor isn't already logged in.
      if (!alreadyAuthed) {
        await register(form.email, form.password, form.name, {
          surname: form.surname,
          ...(isSubscribeMode ? {
            company_name: form.company_name.trim(),
            phone: form.phone.trim(),
          } : {}),
        });
        try {
          if (typeof window !== "undefined" && typeof window.lintrk === "function") {
            window.lintrk("track", { conversion_id: 30800881 });
          }
        } catch (_) { /* analytics ping never blocks flow */ }
      }

      // Free flow → dashboard (unchanged).
      if (!isSubscribeMode) {
        const safeNext = nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//") ? nextPath : null;
        navigate(safeNext || "/dashboard");
        return;
      }

      // Subscribe flow → open Paddle overlay directly, no /billing detour.
      // /api/subscription/checkout also persists company_name + phone
      // server-side (Stage-2 capture side-effect) so returning users
      // don't have to re-enter them.
      const token = localStorage.getItem("esg_token");
      const { data } = await axios.post(
        `${API}/subscription/checkout`,
        { company_name: form.company_name.trim(), phone: form.phone.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!data?.transaction_id) throw new Error("Payment could not start. Please retry.");
      await openPaddleCheckout({
        transactionId: data.transaction_id,
        onCompleted: () => {
          // Give Paddle a beat to finish its animation, then jump to the
          // now-unlocked dashboard with the welcome flash.
          setTimeout(() => navigate("/dashboard?welcome=paid"), 1500);
        },
        onClosed: () => setLoading(false),
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(typeof detail === "string" ? detail : Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(" ")
        : t('reg_err_generic'));
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all";
  const labelClass = "font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block";

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(165deg, #070709 0%, #0A0D12 50%, #070709 100%)" }}>
      <button
        onClick={() => navigate("/")}
        data-testid="auth-back-home"
        className={`absolute top-4 ${isRtl ? "right-4" : "left-4"} z-10 inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-slate-300 hover:text-white font-['Outfit'] text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all`}
      >
        <House size={16} weight="regular" />
        <span className="hidden sm:inline">{t('back_to_home')}</span>
      </button>
      <div className={`absolute top-4 ${isRtl ? "left-4" : "right-4"} z-10`}><LangSwitch /></div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20" style={{ border: "1px solid rgba(139,92,246,0.15)", transform: "translate(30%, -30%)" }} />
      <div className="w-full max-w-lg animate-fade-in-up" data-testid="register-page">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <Logo size="xl" />
        </div>

        {/* ═══ Benefits panel — hidden in Subscribe mode (paying users
             don't need the freemium hook, they want the payment path) ═══ */}
        {!isSubscribeMode && (
        <div
          data-testid="register-benefits-panel"
          className="mb-5 rounded-2xl p-5 sm:p-6 border border-[#01AF9B]/30 bg-gradient-to-br from-[#01AF9B]/[0.09] via-[#0F1418] to-[#111216] shadow-[0_8px_28px_rgba(1,175,155,0.10)] relative overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-[#01AF9B]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-52 h-52 rounded-full bg-[#FBBF24]/[0.06] blur-3xl pointer-events-none" />
          <div className="relative">
            <div
              style={{
                fontSize: 'clamp(9px, 2.6vw, 14px)',
                letterSpacing: 'clamp(1px, 0.5vw, 2.8px)',
              }}
              className="block font-['Outfit'] font-bold uppercase text-[#FBBF24] whitespace-nowrap leading-tight mb-2">
              {t('reg_benefit_sub')}
            </div>
            <h2 className="font-['Outfit'] text-lg sm:text-xl font-bold text-white leading-snug mb-4">
              {t('reg_benefit_headline')}
            </h2>
            <ul className="space-y-2">
              {['reg_benefit_1','reg_benefit_2','reg_benefit_3','reg_benefit_4','reg_benefit_5'].map((k, i) => (
                <li key={k} className={`flex items-start gap-2.5 text-slate-200 text-[13px] leading-snug ${i === 4 ? 'pt-2 mt-1 border-t border-white/[0.06] text-white font-medium' : ''}`}>
                  <CheckCircle size={18} weight="fill" className={`flex-shrink-0 mt-0.5 ${i === 4 ? 'text-[#FBBF24]' : 'text-[#01AF9B]'}`} />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        )}

        {/* ═══ Subscribe-mode price banner (replaces benefits panel) ═══ */}
        {isSubscribeMode && (
          <div
            data-testid="subscribe-price-banner"
            className="mb-5 rounded-2xl p-5 sm:p-6 border border-[#EAB308]/40 bg-gradient-to-br from-[#EAB308]/[0.08] via-[#0F1418] to-[#111216] shadow-[0_8px_28px_rgba(234,179,8,0.12)] text-center"
          >
            <div className="font-['Outfit'] text-[11px] sm:text-[13px] font-bold tracking-[2.5px] uppercase text-[#EAB308] mb-2">
              {t('sub_banner_eyebrow')}
            </div>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white leading-tight mb-1">
              {t('sub_banner_price')}
            </h2>
            <p className="text-slate-400 text-sm">{t('sub_banner_sub')}</p>
          </div>
        )}

        <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <h1 className="font-['Outfit'] text-2xl font-bold text-white mb-2">
            {isSubscribeMode ? t('sub_form_title') : t('reg_title')}
            {!isSubscribeMode && (
              <span className="text-[#FBBF24] text-lg font-semibold ml-3">{t('reg_title_suffix')}</span>
            )}
          </h1>
          <p className="text-slate-400 text-sm mb-6">{isSubscribeMode ? t('sub_form_subtitle') : t('reg_subtitle')}</p>
          {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm" data-testid="register-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>{t('reg_firstname')} *</label>
                <input data-testid="register-name" type="text" value={form.name} onChange={update("name")} required
                  readOnly={isSubscribeMode && !!me}
                  className={`${inputClass} ${isSubscribeMode && !!me ? "opacity-70 cursor-not-allowed" : ""}`}
                  placeholder={t('reg_firstname_ph')} />
              </div>
              <div>
                <label className={labelClass}>{t('reg_surname')} *</label>
                <input data-testid="register-surname" type="text" value={form.surname} onChange={update("surname")} required
                  readOnly={isSubscribeMode && !!me}
                  className={`${inputClass} ${isSubscribeMode && !!me ? "opacity-70 cursor-not-allowed" : ""}`}
                  placeholder={t('reg_surname_ph')} />
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('reg_email')} *</label>
              <input data-testid="register-email" type="email" value={form.email} onChange={update("email")} required
                readOnly={isSubscribeMode && !!me}
                className={`${inputClass} ${isSubscribeMode && !!me ? "opacity-70 cursor-not-allowed" : ""}`}
                placeholder={t('reg_email_ph')} dir="ltr" />
            </div>
            {(!isSubscribeMode || !me) && (
              <div>
                <label className={labelClass}>{t('reg_password')} *</label>
                <div className="relative">
                  <input data-testid="register-password" type={showPw ? "text" : "password"} value={form.password} onChange={update("password")} required className={`${inputClass} ${isRtl ? "pl-10" : "pr-10"}`} placeholder={t('reg_password_ph')} />
                  <button type="button" onClick={() => setShowPw(!showPw)} className={`absolute ${isRtl ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300`}>
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
            {isSubscribeMode && (
              <>
                <div>
                  <label className={labelClass}>{t('sub_company')} *</label>
                  <input data-testid="register-company" type="text" value={form.company_name}
                    onChange={update("company_name")} required className={inputClass}
                    placeholder={t('sub_company_ph')} />
                </div>
                <div>
                  <label className={labelClass}>{t('sub_phone')} *</label>
                  <input data-testid="register-phone" type="tel" value={form.phone}
                    onChange={update("phone")} required className={inputClass}
                    placeholder={t('sub_phone_ph')} dir="ltr" />
                </div>
              </>
            )}
            <button data-testid="register-submit" type="submit" disabled={loading || (isSubscribeMode && !prefillDone)}
              className={`w-full font-['Outfit'] font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                isSubscribeMode
                  ? "bg-[#EAB308] hover:bg-[#FACC15] text-[#0B0F1A] shadow-[0_6px_20px_rgba(234,179,8,0.30)]"
                  : "bg-[#01AF9B] hover:bg-[#018F7E] text-white"
              }`}>
              {loading ? <div className={`w-5 h-5 border-2 ${isSubscribeMode ? "border-[#0B0F1A]" : "border-white"} border-t-transparent rounded-full animate-spin`} /> : (
                <>
                  {isSubscribeMode && <CreditCard size={18} weight="bold" />}
                  <span>{isSubscribeMode ? t('sub_submit') : t('reg_submit')}</span>
                  <ArrowRight size={18} className={isRtl ? "rotate-180" : ""} />
                </>
              )}
            </button>
          </form>
          <p className="text-center text-slate-500 text-sm mt-5">
            {t('reg_have_account')} <Link to={nextPath ? `/login?next=${encodeURIComponent(nextPath)}` : "/login"} className="text-[#01AF9B] hover:text-[#01AF9B]/80 font-medium" data-testid="go-login">{t('reg_signin_link')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
